import type { NewNotificationItem } from './NewNotificationCard';
import type { ViewShareNotificationItem, ViewShareNotificationValue } from '../../types';

export type CountState = {
  loadedCount: number;
  totalCount: number;
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export function getViewShareNotificationCreatorName(item: ViewShareNotificationItem): string {
  const firstName = item?.value?.created_by_user?.firstname ?? '';
  const lastName = item?.value?.created_by_user?.lastname ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || '-';
}

function getLocalizedViewManagerScreenName(
  value: Partial<ViewShareNotificationValue> | null | undefined,
  language: string,
): string {
  const lang = (language || 'en').split('-')[0];
  if (lang === 'de') {
    return value?.screenDisplayNameDe ?? value?.screenDisplayNameEn ?? '';
  }

  return value?.screenDisplayNameEn ?? value?.screenDisplayNameDe ?? '';
}

export function getLocalizedViewManagerLabel(
  item: ViewShareNotificationItem,
  t: TranslateFn,
  language: string,
): string {
  const firstName = item?.value?.created_by_user?.firstname ?? '';
  const lastName = item?.value?.created_by_user?.lastname ?? '';
  const viewName = item?.value?.viewName ?? '';
  const screenName = getLocalizedViewManagerScreenName(item?.value, language);

  return `${firstName} ${lastName} ${t('notification_panel.shared_view')} "${viewName}" ${t(
    'notification_panel.with_you',
  )} ${t('notification_panel.for')} ${screenName}`;
}

export function reorderLoadedGeneralNotifications(items: NewNotificationItem[]): NewNotificationItem[] {
  const unread = items.filter((item) => item.status === 'UNREAD');
  const read = items
    .filter((item) => item.status === 'READ')
    .sort((left, right) => {
      const leftUpdatedAt = left.updateDate ? new Date(left.updateDate).getTime() : 0;
      const rightUpdatedAt = right.updateDate ? new Date(right.updateDate).getTime() : 0;

      if (leftUpdatedAt !== rightUpdatedAt) {
        return leftUpdatedAt - rightUpdatedAt;
      }

      const leftId = left.notificationId ?? 0;
      const rightId = right.notificationId ?? 0;
      return leftId - rightId;
    });

  return [...unread, ...read];
}

export function moveGeneralNotificationToUnreadTop(
  items: NewNotificationItem[],
  userNotificationId: number,
): NewNotificationItem[] {
  const itemIndex = items.findIndex((item) => item.userNotificationId === userNotificationId);
  if (itemIndex <= 0) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(itemIndex, 1);
  nextItems.unshift(movedItem);
  return nextItems;
}

export function getCountsAfterRemoval({ loadedCount, totalCount }: CountState) {
  return {
    nextLoadedCount: Math.max(loadedCount - 1, 0),
    nextTotalCount: Math.max(totalCount - 1, 0),
  };
}
